import type { Selection } from "react-aria-components";
import { useCallback, useMemo } from "react";

import { showToast } from "@/providers/ToastProvider";

import type { AdaptedQueueRow } from "../helpers/adapters";
import { isStatusSettled } from "../helpers/isStatusSettled";

const MAX_SELECTION = 15;

type UseCosignatureSelectionParams = {
  onSelectedRowsChange: (rows: AdaptedQueueRow[]) => void;
  rows: AdaptedQueueRow[];
  selectedIds: string[];
  sortedRows: AdaptedQueueRow[];
};

export function useCosignatureSelection({
  onSelectedRowsChange,
  rows,
  selectedIds,
  sortedRows,
}: UseCosignatureSelectionParams) {
  const disabledKeys = useMemo(() => {
    const settled = new Set(rows.filter((r) => isStatusSettled(r.status)).map((r) => r.id));

    if (selectedIds.length >= MAX_SELECTION) {
      rows
        .filter((r) => !isStatusSettled(r.status) && !selectedIds.includes(r.id))
        .forEach((r) => settled.add(r.id));
    }

    return settled;
  }, [rows, selectedIds]);

  const onSelectionChange = useCallback(
    (selection: Selection) => {
      let resolvedRows: AdaptedQueueRow[];

      if (selection === "all") {
        resolvedRows = sortedRows.filter((r) => !isStatusSettled(r.status)).slice(0, MAX_SELECTION);
      } else {
        const selectedSet = selection as Set<string>;

        resolvedRows = rows.filter((r) => selectedSet.has(r.id)).slice(0, MAX_SELECTION);
      }

      if (resolvedRows.length === MAX_SELECTION) {
        showToast([
          {
            heading: "Maximum selection reached",
            text: "You can select up to 15 notes at a time.",
            variant: "warning",
          },
        ]);
      }

      onSelectedRowsChange(resolvedRows);
    },
    [rows, sortedRows, onSelectedRowsChange],
  );

  // stable Set reference prevents react-aria's TableCollection from rebuilding on every render
  const selectedKeys = useMemo(() => new Set(selectedIds), [selectedIds]);

  return { disabledKeys, onSelectionChange, selectedKeys };
}
